import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Card, Button, Space, Input, message, Popconfirm, Tag } from 'antd';
import { SoundOutlined, SearchOutlined, ReloadOutlined, DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { GET_ALL_VOCALS } from '../../graphql/queries';
import { DELETE_VOCAL } from '../../graphql/mutation';
import './Vocals.css';

const Vocals = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [userId, setUserId] = useState(searchParams.get('userId') || '');
  const [search, setSearch] = useState('');

  const { loading, data, refetch } = useQuery(GET_ALL_VOCALS, {
    variables: {
      page,
      pageSize,
      userId: userId || undefined,
      search: search || undefined,
    },
    fetchPolicy: 'network-only',
  });

  const [deleteVocal] = useMutation(DELETE_VOCAL, {
    onCompleted: () => {
      message.success('Vocal deleted successfully');
      refetch();
    },
    onError: (error) => {
      message.error(`Failed to delete vocal: ${error.message}`);
    }
  });

  const handleTableChange = (pagination) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleReset = () => {
    setUserId('');
    setSearch('');
    setPage(1);
    setSearchParams({});
  };

  const handleSearch = () => {
    const params = {};
    if (userId) params.userId = userId;
    setSearchParams(params);
    refetch();
  };

  const handleDelete = (id) => {
    deleteVocal({ variables: { id } });
  };

  const columns = [
    {
      title: 'Vocal',
      key: 'vocal',
      width: 100,
      render: () => (
        <div className="vocal-placeholder">
          <SoundOutlined style={{ fontSize: 24 }} />
        </div>
      )
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (title) => (
        <div style={{ fontWeight: 500 }}>{title || 'Untitled Vocal'}</div>
      )
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 180,
      render: (userId) => (
        <div style={{ fontSize: 12, fontFamily: 'monospace' }}>
          {userId || 'Unknown'}
        </div>
      )
    },
    {
      title: 'Gender',
      dataIndex: 'vocalGender',
      key: 'vocalGender',
      width: 100,
      render: (vocalGender) => (
        <Tag color={vocalGender === 'male' ? 'blue' : 'pink'}>
          {vocalGender === 'male' ? 'Male' : 'Female'}
        </Tag>
      )
    },
    {
      title: 'Visibility',
      dataIndex: 'isVisible',
      key: 'isVisible',
      width: 100,
      render: (isVisible) => (
        <Tag color={isVisible ? 'success' : 'default'}>
          {isVisible ? 'Visible' : 'Hidden'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/vocals/${record.id}`)}
          >
            View
          </Button>
          <Popconfirm
            title="Are you sure to delete this vocal?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const vocals = data?.getAllVocals || [];

  return (
    <div className="vocals-page">
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Vocals Management</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/vocals/create')}
          >
            Create Vocal
          </Button>
        </div>

        <div className="filters-section">
          <Space wrap style={{ marginBottom: 16 }}>
            <Input
              style={{ width: 200 }}
              placeholder="User ID / RC User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              prefix={<SearchOutlined />}
            />

            <Input
              style={{ width: 250 }}
              placeholder="Search by title"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={<SearchOutlined />}
            />

            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              Search
            </Button>

            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>

            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={vocals}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: vocals.length,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} vocals`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1170 }}
        />
      </Card>
    </div>
  );
};

export default Vocals;
